import React, { useEffect } from 'react';
import {
  batch,
  computed,
  Signal,
  useSignal,
  ReadonlySignal,
  useSignalEffect,
} from '@preact/signals-react';
import { NumberParam, useQueryParam } from 'use-query-params';
import moment from 'moment';
import { Opt } from '@/features/details/date-pick/quick-range-select';
import { ExpVersion } from '@/webapi/use-analytics-api';
import { Experience } from '@/webapi/use-experience-api';
import { isValid } from '@/utils/data_freshness';
import { CustomDateParam } from '@/utils/date-params';

export interface DatePickerContextShape {
  step: Signal<Opt>;
  setStep: (x: Opt) => void;
  setRanges: (
    x: Array<{ startDate: Date; endDate: Date; key: string }>,
  ) => void;
  versions: Array<ExpVersion>;
  setVersion: (value: ExpVersion | undefined) => void;
  startDate: Signal<Date>;
  endDate: Signal<Date>;
  version: ReadonlySignal<ExpVersion | undefined>;
  ranges: ReadonlySignal<
    Array<{ startDate: Date; endDate: Date; key: string }>
  >;
  showVersionsSelect: Signal<boolean>;
  showQuickSelect: Signal<boolean>;
  showCustomDatePicker: Signal<boolean>;
  quickSelect: () => void;
  customRange: () => void;
  versionsSelect: () => void;
  loading: Signal<boolean>;
}

export const DatePickerContext = React.createContext(
  {} as DatePickerContextShape,
);

export function newDatePickerContext(experience: Experience) {
  const [qStep, setQStep] = useQueryParam<Opt>(`o`, NumberParam);
  const [qVer, setQVer] = useQueryParam<number>(`v`, NumberParam);
  const [qStart, setQStart] = useQueryParam(`s`, CustomDateParam);
  const [qEnd, setQEnd] = useQueryParam(`e`, CustomDateParam);

  const expEndDate = getExpEndDate(experience);

  const versionsCache = React.useRef<Array<ExpVersion>>(null);
  const versions = React.useMemo(() => {
    const computed = getExperienceVersions(experience, expEndDate);
    if (computed?.length) {
      versionsCache.current = computed;
      return computed;
    }
    return versionsCache.current;
  }, [experience, expEndDate]);

  const initialStart = React.useMemo(
    () => calcDefaultStartDate(qStart, experience),
    [],
  );

  const initialEnd = React.useMemo(() => qEnd || getUtcDate(0), []);

  const step = useSignal(qStep);
  const versionNum = useSignal(qVer);
  const start = useSignal(initialStart);
  const end = useSignal(initialEnd);
  const loading = useSignal(false);

  const showQuickSelect = useSignal(shoQuickSelect(step.peek()));
  const showCustomDatePicker = useSignal(showCustomRange(step.peek()));
  const showVersionsSelect = useSignal(showPrevVersion(step.peek()));

  useEffect(() => {
    setTimeout(() => {
      batch(() => {
        if (step.value !== qStep) step.value = qStep;
        if (versionNum.value !== qVer) versionNum.value = qVer;
        if (start.value?.valueOf() !== qStart?.valueOf())
          start.value = qStart || initialStart;
        if (end.value?.valueOf() !== qEnd?.valueOf())
          end.value = qEnd || initialEnd;

        const newShowQuick = shoQuickSelect(qStep);
        if (showQuickSelect.value !== newShowQuick)
          showQuickSelect.value = newShowQuick;

        const newShowCustom = showCustomRange(qStep);
        if (showCustomDatePicker.value !== newShowCustom)
          showCustomDatePicker.value = newShowCustom;

        const newShowVer = showPrevVersion(qStep);
        if (showVersionsSelect.value !== newShowVer)
          showVersionsSelect.value = newShowVer;

        if (!qStep) {
          setStepAction(Opt.Current);
        } else if (qStep && (!qStart || !qEnd)) {
          setStepAction(qStep);
        }
      });
      loading.value = true;
    }, 1000);
  }, []);

  const version = computed(() => {
    if (experience?.isHidden) return undefined;
    return versions?.find((v) => v.version === versionNum.value);
  });

  const ranges = computed(() => [
    {
      startDate: start.value,
      endDate: end.value,
      key: `rollup`,
    },
  ]);

  const setStepAction = (newStep: Opt) => {
    batch(() => {
      if (!newStep) newStep = Opt.Current;
      step.value = newStep;
      applyStepLogic(newStep, start, end, versionNum, versions);

      showQuickSelect.value = shoQuickSelect(newStep);
      showCustomDatePicker.value = showCustomRange(newStep);
      showVersionsSelect.value = showPrevVersion(newStep);
    });
  };

  const setRangesAction = (
    x: Array<{ startDate: Date; endDate: Date; key: string }>,
  ) => {
    batch(() => {
      const r = x[0];
      if (r) {
        const offset = moment().utcOffset() / 60;
        start.value = moment(r.startDate).add(offset, `hours`).toDate();
        end.value = moment(r.endDate).add(offset, `hours`).toDate();
      }
    });
  };

  const setVersionAction = (v: ExpVersion | undefined) => {
    if (experience?.isHidden) return;
    batch(() => {
      versionNum.value = v?.version;
      if (v) {
        start.value = new Date(v.startDate);
        end.value = getVersionEndDate(v);
      }
    });
  };

  const quickSelect = () => {
    batch(() => {
      showVersionsSelect.value = false;
      showCustomDatePicker.value = false;
      showQuickSelect.value = true;
    });
  };

  const customRange = () => {
    batch(() => {
      showVersionsSelect.value = false;
      showCustomDatePicker.value = true;
      showQuickSelect.value = false;
    });
  };

  const versionsSelect = () => {
    batch(() => {
      showVersionsSelect.value = true;
      showCustomDatePicker.value = false;
      showQuickSelect.value = false;
    });
  };
  // sync to query
  useSignalEffect(() => {
    setQStep(step.value);
    setQVer(versionNum.value);
    setQStart(start.value);
    setQEnd(end.value);
  });

  return {
    quickSelect,
    customRange,
    versionsSelect,
    setRanges: setRangesAction,
    setVersion: setVersionAction,
    showQuickSelect,
    showVersionsSelect,
    showCustomDatePicker,
    ranges,
    endDate: end,
    startDate: start,
    version,
    step,
    versions,
    setStep: setStepAction,
    loading,
  };
}

function applyStepLogic(
  step: Opt,
  start: Signal<Date>,
  end: Signal<Date>,
  versionNum: Signal<number | undefined>,
  versions: Array<ExpVersion>,
) {
  switch (step) {
    case Opt.Current:
      {
        const current = versions?.find((v) => v?.isCurrent);
        if (current) {
          const s = new Date(current.startDate);
          const e = new Date(getVersionEndDate(current));
          if (isValid(s) && isValid(e)) {
            start.value = s;
            end.value = e;
          }
          versionNum.value = current.version;
        }
      }
      break;
    case Opt.Today:
      start.value = getUtcDate(0);
      end.value = getUtcDate(0);
      versionNum.value = undefined;
      break;
    case Opt.Yesterday:
      start.value = getUtcDate(1);
      end.value = getUtcDate(1);
      versionNum.value = undefined;
      break;
    case Opt.LastWeek:
      start.value = getUtcDate(7);
      end.value = getUtcDate(0);
      versionNum.value = undefined;
      break;
    case Opt.LastMonth:
      start.value = getUtcDate(30);
      end.value = getUtcDate(0);
      versionNum.value = undefined;
      break;
    case Opt.OverAll:
      {
        const firstVer = versions?.find((v) => v.version === 1);
        if (firstVer && firstVer.startDate) {
          const s = new Date(firstVer.startDate);
          if (isValid(s)) {
            start.value = s;
            end.value = getUtcDate(0);
          }
        }
        versionNum.value = undefined;
      }
      break;
    default:
      break;
  }
}

function calcDefaultStartDate(s: Date, experience: Experience) {
  return (
    s ||
    (experience?.metricsStartAt && new Date(experience?.metricsStartAt)) ||
    (experience?.publishedAt && new Date(experience?.publishedAt)) ||
    getUtcDate(0)
  );
}

function shoQuickSelect(step: Opt) {
  return [
    Opt.Current,
    Opt.Today,
    Opt.Yesterday,
    Opt.LastWeek,
    Opt.LastMonth,
    Opt.OverAll,
  ].includes(step);
}

function showCustomRange(step: Opt) {
  return step === Opt.CustomRange;
}

function showPrevVersion(step: Opt) {
  return step === Opt.PreviousVersion;
}

function getExpEndDate(experience: Experience): Date | undefined {
  const experienceEndDate = new Date(
    experience?.archivedAt || experience?.endDate,
  );
  return isValid(experienceEndDate) ? experienceEndDate : undefined;
}

function getVersionEndDate(version: ExpVersion) {
  let endDate = new Date(version?.endDate);
  if (version?.expEndDate) {
    endDate = new Date(
      Math.min(version?.expEndDate?.valueOf(), endDate?.valueOf()),
    );
  }
  return endDate;
}

function getExperienceVersions(experience: Experience, expEndDate: Date) {
  return experience.versions?.map((v) => {
    if (v?.version === experience?.currentVersion?.version) {
      return {
        ...v,
        isCurrent: true,
        expEndDate,
      };
    }
    return { ...v, expEndDate };
  }) as Array<ExpVersion>;
}

function getUtcDate(daysAgo = 0) {
  const now = moment();
  const offset = now.utcOffset() / 60;
  return moment.utc().add(offset, `hours`).subtract(daysAgo, `days`).toDate();
}
